<template lang="pug">
  .comments.profile-content()
    p(
      v-if="state === 'error'"
    ) Произошла ошибка при загрузке комментариев.

    .comments__no-data(
        v-else-if="state === 'no-data'"
    )
      BannerNoData(
        title="Нет комментариев"
        :text="isOwnProfile ? 'Напишите первый!' : undefined"
      )

    .comments__list(v-else)
      CommentCard(
        v-for="item of items"
        :key="item.id"
        :avatar="item.avatar"
        :author="item.author"
        :date-gmt="item.date_gmt"
        :likesCount="item.users_liked_cnt"
        :dislikesCount="item.users_disliked_cnt"
        :content="item.content"
        :title="item.post_title || ''"
        :commentId="item.id"
        :showMedia="true"
        :files="item.files"
        :videos="item.videos"
        :postSlug="item.post && item.post.slug"
        :postType="item.post && item.post.type"
      )
      IntersectionObserver(
        ref="observer"
        v-if="state === 'fetching' || state === 'pending'"
        @appear="fetchComments"
      )
        ProfileCardPlaceholder
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import IntersectionObserver from '@/components/lh-ui/IntersectionObserver/IntersectionObserver.vue';

  import CommentCard from '~/components/CommentCard/CommentCard.vue';
  import { fetchPublicUserComments } from '@devhacker/shared/features/profile/api';

  const BannerNoData = defineAsyncComponent(
    () => import('~/components/BannerNoData/BannerNoData.vue'),
  );
  const ProfileCardPlaceholder = defineAsyncComponent(
    () => import('~/placeholders/ProfileCardPlaceholder/ProfileCardPlaceholder.vue'),
  );

  type ProfileCommentsResponse = {
    data: CommentItem[];
    meta?: { total?: number };
  };

  type ProfileDataComments = {
    items: CommentItem[];
    total: number;
    page: number;
    state: 'pending' | 'fetching' | 'no-data' | 'fulfilled' | 'error';
  };

  const COMMENTS_COUNT_ON_PAGE = 30;

  export default defineNuxtComponent({
    name: 'ProfileCommentsPage',

    components: {
      IntersectionObserver,
      CommentCard,
      BannerNoData,
      ProfileCardPlaceholder,
    },

    scrollToTop: true,

    props: {
      userId: {
        type: String as PropType<string>,
        required: true,
      },
      authorName: {
        type: String as PropType<string | null>,
        default: null,
      },
      isOwnProfile: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
      isAuthor: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
      eventCategory: {
        type: String as PropType<string>,
        required: true,
      },
    },

    data(): ProfileDataComments {
      return {
        items: [],
        total: 0,
        state: 'pending',
        page: 1,
      };
    },

    // TODO: Check if neccesary
    // watch: {
    //   items () {
    //     // Иногда если пользователь доскроллил страницу до конца,
    //     // то новые элементы добавляются сверху и не происходит скролл страницы вверх.
    //     // Поэтому если плейсхолдер сейчас виден на экране или находится выше окна, загружаем следующие
    //     const observerEl = (this.$refs.observer as Vue).$el as HTMLElement;
    //     if (isElementInViewport(observerEl) || observerEl.getBoundingClientRect().top <= 0) {
    //       this.fetchComments();
    //     }
    //   },
    // },

    methods: {
      async fetchComments(): Promise<void> {
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 1000);
        });
        if (this.state !== 'pending') {
          return;
        }

        try {
          this.state = 'fetching';
          const { page } = this;
          const response = await fetchPublicUserComments<ProfileCommentsResponse>(this.userId, {
            page,
            limit: COMMENTS_COUNT_ON_PAGE,
          });

          this.total = response.meta?.total ?? 0;
          const comments = response.data;
          if (comments) {
            this.items.push(...comments);
          }
          const noData = this.total === 0;
          const allPostsLoaded = this.items.length === this.total;
          if (noData) {
            this.state = 'no-data';
          } else if (allPostsLoaded) {
            this.state = 'fulfilled';
          } else {
            this.page += 1;
            this.state = 'pending';
          }
        } catch {
          this.state = 'error';
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .comments {
    width: 100%;
    display: flex;

    &__no-data {
      width: 100%;
      padding: 40px 0 80px;
      text-align: center;

      @include tablet {
        padding: 80px 0 120px;
      }
    }

    &__list {
      width: 100%;
    }

    &__item {
      @include big-tablet {
        padding: 24px;

        &:not(:last-child):before {
          left: 24px;
          width: calc(100% - 48px);
        }
      }
    }
  }
</style>
